// /trading/macro — Wirtschaftsnews mit EVE-Pre-Market-Take.
// Design-Quelle: 21st.dev/r/ravikatiyar162/economic-calendar (Doc-approved
// 2026-05-17, Skill upro-redesign aktiv). Deutsche UI, glass + amber Palette.

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  Calendar,
  Check,
  Copy,
  Download,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import {
  EconomicCalendar,
  type EconomicEvent,
  type Impact,
} from '@/components/markets/economic-calendar';
import { buildMacroDisplayText, buildMacroFragEvePrompt } from '@/lib/macro-frag-eve-prompt';
import { formatMacroTime } from '@/lib/macro-time';
import {
  buildCalendarSubscribeUrl,
  fetchLatestForecast,
  fetchUpcomingReleases,
  triggerForecastNow,
  ASSET_KEYS,
  type AssetKey,
  type DatedMacroRelease,
  type Dir,
  type MacroCountry,
  type MacroForecast,
  type MacroScenario,
  type PerEventForecast,
  type PerEventScenario,
} from '@/lib/macro-api';

type RangeKey = 'today' | 'week' | 'two-weeks';

const RANGE_DAYS: Record<RangeKey, number> = {
  today: 1,
  week: 7,
  'two-weeks': 14,
};

const RANGE_LABELS: Record<RangeKey, string> = {
  today: 'Heute',
  week: '7 Tage',
  'two-weeks': '14 Tage',
};

type ImpactFilter = 'all' | 'mid_high' | 'high_only';

const IMPACT_FILTER_LABEL: Record<ImpactFilter, string> = {
  all: 'Alle',
  mid_high: 'Wichtig',
  high_only: 'Nur Hoch',
};

function passesImpact(importance: 'low' | 'medium' | 'high', filter: ImpactFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'mid_high') return importance === 'medium' || importance === 'high';
  return importance === 'high';
}

const COUNTRY_META: Record<MacroCountry, { flag: string; label: string }> = {
  US: { flag: '🇺🇸', label: 'USA' },
  EU: { flag: '🇪🇺', label: 'Eurozone' },
  DE: { flag: '🇩🇪', label: 'Deutschland' },
  GB: { flag: '🇬🇧', label: 'UK' },
  JP: { flag: '🇯🇵', label: 'Japan' },
  AU: { flag: '🇦🇺', label: 'Australien' },
  CA: { flag: '🇨🇦', label: 'Kanada' },
  CH: { flag: '🇨🇭', label: 'Schweiz' },
  CN: { flag: '🇨🇳', label: 'China' },
  NZ: { flag: '🇳🇿', label: 'Neuseeland' },
};

const ALL_COUNTRIES: ReadonlyArray<MacroCountry> = [
  'US', 'EU', 'DE', 'GB', 'JP', 'AU', 'CA', 'CH', 'CN', 'NZ',
];

const BIAS_LABEL: Record<MacroForecast['overall_bias'], string> = {
  bullish: 'Bullisch',
  bearish: 'Bärisch',
  neutral: 'Neutral',
};

const BIAS_TONE: Record<MacroForecast['overall_bias'], string> = {
  bullish: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
  bearish: 'border-rose-500/40 bg-rose-500/15 text-rose-300',
  neutral: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
};

const SCENARIO_LABEL: Record<MacroScenario['label'], string> = {
  beat: 'Übertrifft',
  inline: 'Wie erwartet',
  miss: 'Verfehlt',
};

function fmtNumber(n: number | null, unit: string | null = null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  const formatted =
    Math.abs(n) >= 1000 ? n.toLocaleString('de-DE') : n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  return unit ? `${formatted} ${unit}` : formatted;
}

function fmtAge(iso: string): string {
  const sec = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (sec < 60) return `vor ${sec} s`;
  if (sec < 3600) return `vor ${Math.round(sec / 60)} min`;
  if (sec < 86400) return `vor ${Math.round(sec / 3600)} h`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function fmtDateHeader(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function importanceToImpact(i: 'low' | 'medium' | 'high'): Impact {
  return i;
}

function releaseToEvent(r: DatedMacroRelease): EconomicEvent {
  const t = formatMacroTime(r.release_time_et, r.date);
  return {
    countryCode: r.country,
    date: r.date,
    timeBerlin: t.berlin,
    timeEt: t.et,
    eventName: r.indicator,
    actual: r.actual !== null ? fmtNumber(r.actual, r.unit) : null,
    forecast: r.consensus !== null ? fmtNumber(r.consensus, r.unit) : null,
    prior: r.previous !== null ? fmtNumber(r.previous, r.unit) : null,
    impact: importanceToImpact(r.importance),
  };
}

export function TradingMacroPage() {
  const { isOwner } = useAuth();
  const accessToken = useAuthStore(selectAccessToken);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const [releases, setReleases] = useState<DatedMacroRelease[]>([]);
  const [forecast, setForecast] = useState<MacroForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [range, setRange] = useState<RangeKey>('two-weeks');
  const [countries, setCountries] = useState<MacroCountry[]>(['US', 'DE', 'EU']);
  const [impactFilter, setImpactFilter] = useState<ImpactFilter>('all');

  async function reload(
    currentRange: RangeKey = range,
    currentCountries: MacroCountry[] = countries,
  ) {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [upcoming, latest] = await Promise.all([
        fetchUpcomingReleases(accessToken, RANGE_DAYS[currentRange], currentCountries),
        fetchLatestForecast(accessToken),
      ]);
      setReleases(upcoming.releases ?? []);
      setForecast(latest.forecast);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  function toggleCountry(c: MacroCountry) {
    const next = countries.includes(c) ? countries.filter((x) => x !== c) : [...countries, c];
    const safe = next.length > 0 ? next : countries;
    setCountries(safe);
    void reload(range, safe);
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function handleTriggerNow() {
    if (!accessToken) return;
    setTriggering(true);
    try {
      const res = await triggerForecastNow(accessToken);
      if (!res.saved) setError(res.reason ?? 'Vorhersage konnte nicht gespeichert werden');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Auslösen fehlgeschlagen');
    } finally {
      setTriggering(false);
    }
  }

  // Client-side Impact-Filter (Doc 2026-05-17): kein Backend-Roundtrip
  // nötig, alle Events sind eh schon geladen.
  const filteredReleases = releases.filter((r) => passesImpact(r.importance, impactFilter));

  // Gruppieren nach Datum + sortieren high-impact zuerst, dann nach Uhrzeit
  const groupedByDate = (() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const order = { high: 0, medium: 1, low: 2 } as const;
    const map = new Map<string, DatedMacroRelease[]>();
    for (const r of filteredReleases) {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date)!.push(r);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, items]) => ({
        date,
        isToday: date === todayStr,
        items: items.sort((a, b) => {
          const o = order[a.importance] - order[b.importance];
          if (o !== 0) return o;
          return (a.release_time_et ?? '').localeCompare(b.release_time_et ?? '');
        }),
      }));
  })();

  const highCount = filteredReleases.filter((r) => r.importance === 'high').length;
  const totalLoaded = releases.length;
  const hiddenByFilter = totalLoaded - filteredReleases.length;

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-6xl space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <Activity className="size-5 text-amber-400" />
              <h1 className="text-xl font-bold">Wirtschaftsnews</h1>
            </div>
            <p className="mt-1 text-[12px] text-neutral-500">
              Heutige + kommende Releases mit EVE-Vorhersage zur Marktreaktion. Cron läuft Mo-Fr 8:30 ET vor US-Eröffnung.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void reload()}
              aria-label="Aktualisieren"
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1.5 text-[12px] text-neutral-300 hover:bg-white/5"
            >
              <RefreshCw className={`size-3 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
            {isOwner ? (
              <button
                type="button"
                onClick={() => void handleTriggerNow()}
                disabled={triggering}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/15 px-2.5 py-1.5 text-[12px] font-bold text-amber-300 hover:bg-amber-500/25 disabled:opacity-50"
              >
                <Sparkles className={`size-3 ${triggering ? 'animate-pulse' : ''}`} />
                {triggering ? 'Wird erstellt…' : 'Vorhersage jetzt'}
              </button>
            ) : null}
          </div>
        </header>

        {error ? (
          <div className="flex items-start gap-2 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {forecast ? (
          <ForecastCard forecast={forecast} />
        ) : (
          <div className="glass-strong rounded-2xl border border-dashed border-white/10 p-6 text-center">
            <p className="text-[13px] text-neutral-400">
              Noch keine EVE-Vorhersage für heute. Der Cron läuft Mo-Fr um 8:30 ET (13:30 UTC).
            </p>
            {isOwner ? (
              <p className="mt-2 text-[11px] text-neutral-500">
                Als Owner kannst du oben „Vorhersage jetzt" klicken um eine Ad-hoc-Analyse zu generieren.
              </p>
            ) : null}
          </div>
        )}

        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wider text-neutral-400">
              <Calendar className="size-3" />
              Kalender · {RANGE_LABELS[range]}
            </h2>
            <span className="text-[11px] text-neutral-500">
              {filteredReleases.length} von {totalLoaded} · {highCount} Hoher Einfluss
              {hiddenByFilter > 0 ? ` · ${hiddenByFilter} ausgeblendet` : ''}
            </span>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">Zeitraum:</span>
              {(Object.keys(RANGE_DAYS) as RangeKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setRange(k);
                    void reload(k, countries);
                  }}
                  className={
                    range === k
                      ? 'rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300'
                      : 'rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-white/5'
                  }
                >
                  {RANGE_LABELS[k]}
                </button>
              ))}
            </div>
            <span className="text-neutral-700">|</span>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">Regionen:</span>
              {ALL_COUNTRIES.map((c) => {
                const active = countries.includes(c);
                const meta = COUNTRY_META[c];
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCountry(c)}
                    title={meta.label}
                    className={
                      active
                        ? 'inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300'
                        : 'inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-white/5'
                    }
                  >
                    <span>{meta.flag}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <span className="text-neutral-700">|</span>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">Einfluss:</span>
              {(Object.keys(IMPACT_FILTER_LABEL) as ImpactFilter[]).map((f) => {
                const tone =
                  f === 'high_only'
                    ? 'rose'
                    : f === 'mid_high'
                      ? 'amber'
                      : 'neutral';
                const activeClass =
                  tone === 'rose'
                    ? 'rounded-md border border-rose-500/40 bg-rose-500/15 px-2 py-0.5 text-[11px] font-bold text-rose-300'
                    : tone === 'amber'
                      ? 'rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-300'
                      : 'rounded-md border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[11px] font-bold text-neutral-300';
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setImpactFilter(f)}
                    className={
                      impactFilter === f
                        ? activeClass
                        : 'rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400 hover:bg-white/5'
                    }
                  >
                    {IMPACT_FILTER_LABEL[f]}
                  </button>
                );
              })}
            </div>
          </div>

          <CalendarSubscribeBanner accessToken={accessToken} countries={countries} />

          {loading && releases.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-neutral-500">Lade Kalender…</div>
          ) : groupedByDate.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-[12px] text-neutral-500">
              Keine Ereignisse im gewählten Zeitraum.
            </div>
          ) : (
            <div className="space-y-1">
              {groupedByDate.map((group) => (
                <EconomicCalendar
                  key={group.date}
                  title={fmtDateHeader(group.date)}
                  isToday={group.isToday}
                  events={group.items.map(releaseToEvent)}
                  onAskEve={(event) => {
                    const orig = group.items.find((r) => r.indicator === event.eventName);
                    const input = {
                      eventName: event.eventName,
                      date: orig?.date ?? group.date,
                      timeBerlin: event.timeBerlin,
                      timeEt: event.timeEt,
                      country: orig?.country,
                      consensus: event.forecast,
                      previous: event.prior,
                      actual: event.actual,
                    };
                    // Läuft über Claude. Event-Daten stecken schon
                    // im Prompt, Web-Suche unnötig (+ Credits gespart).
                    askEve(buildMacroFragEvePrompt(input), {
                      displayText: buildMacroDisplayText(input),
                    });
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const ASSET_LABELS: Record<AssetKey, { label: string; unit: 'pct' | 'bp' }> = {
  DXY: { label: 'DXY', unit: 'pct' },
  EURUSD: { label: 'EUR/USD', unit: 'pct' },
  BTC: { label: 'BTC', unit: 'pct' },
  NDX: { label: 'NDX', unit: 'pct' },
  SPX: { label: 'SPX', unit: 'pct' },
  DAX: { label: 'DAX', unit: 'pct' },
  GOLD: { label: 'Gold', unit: 'pct' },
  US10Y: { label: 'US10Y', unit: 'bp' },
  OIL: { label: 'Öl', unit: 'pct' },
};

function ForecastCard({ forecast }: { forecast: MacroForecast }) {
  const isV2 = (forecast.events?.length ?? 0) > 0;
  // pre_note: V2 dieses Feld, V1 summary. Cron schreibt pre_note ins
  // summary-D1-Field, also funktioniert beides via Fallback.
  const preNote = forecast.pre_note || forecast.summary || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      className="glass-strong rounded-2xl border border-amber-500/20 p-5"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-amber-400" />
          <h2 className="text-[13px] font-bold uppercase tracking-wider text-amber-300">
            EVE Pre-Trade-Briefing · {forecast.target_date}
          </h2>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className={`rounded-full border px-2 py-0.5 font-bold uppercase ${BIAS_TONE[forecast.overall_bias]}`}>
            {BIAS_LABEL[forecast.overall_bias]}
          </span>
          <span className="text-neutral-500">{fmtAge(forecast.generated_at)}</span>
        </div>
      </div>

      {preNote ? (
        <p className="mb-4 text-[13px] leading-relaxed text-neutral-200">{preNote}</p>
      ) : null}

      {isV2 && forecast.events ? (
        <div className="space-y-4">
          {forecast.events.map((ev, i) => (
            <EventForecastBlock key={`${ev.event_name}-${i}`} event={ev} />
          ))}
        </div>
      ) : forecast.scenarios && forecast.scenarios.length > 0 ? (
        // V1-Fallback: alte 3-Tile-Ansicht für Forecasts ohne events
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          {forecast.scenarios.map((s, i) => (
            <LegacyScenarioTile key={i} scenario={s} />
          ))}
        </div>
      ) : null}

      <div className="mt-4 border-t border-white/5 pt-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          Trader-Fazit
        </div>
        <p className="text-[12px] leading-relaxed text-neutral-200">{forecast.rationale}</p>
      </div>
    </motion.div>
  );
}

function EventForecastBlock({ event }: { event: PerEventForecast }) {
  const t = formatMacroTime(event.release_time_et);
  return (
    <article className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-[14px] font-bold text-neutral-100">{event.event_name}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-3 text-[11px] text-neutral-500">
            <span className="tabular">
              {t.berlin}
              {' · '}
              <span className="opacity-80">{t.et} ET</span>
            </span>
            <span>
              Prognose: <span className="text-amber-300 tabular">{event.consensus || '—'}</span>
            </span>
            <span>
              Vorher: <span className="text-neutral-300 tabular">{event.previous || '—'}</span>
            </span>
          </div>
        </div>
      </header>

      {event.priced_in_summary ? (
        <div className="mb-3 rounded-md border border-amber-500/10 bg-amber-500/[0.04] px-3 py-1.5 text-[11px] text-amber-200/80">
          <span className="font-bold uppercase tracking-wider text-amber-300/80">Eingepreist:</span>{' '}
          {event.priced_in_summary}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        {event.scenarios.map((s, i) => (
          <ScenarioColumn key={`${event.event_name}-${s.label}-${i}`} scenario={s} />
        ))}
      </div>
    </article>
  );
}

function ScenarioColumn({ scenario }: { scenario: PerEventScenario }) {
  const labelTone =
    scenario.label === 'beat'
      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300'
      : scenario.label === 'miss'
        ? 'border-rose-500/40 bg-rose-500/15 text-rose-300'
        : 'border-amber-500/30 bg-amber-500/15 text-amber-300';

  // Asset-Keys nur die rendern, für die EVE Reactions geliefert hat
  const rows = ASSET_KEYS.filter((k) => scenario.asset_reactions?.[k] !== undefined);

  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${labelTone}`}>
          {SCENARIO_LABEL[scenario.label]}
        </span>
        <span className="rounded bg-neutral-800/70 px-1.5 py-0.5 text-[11px] font-bold tabular text-amber-300">
          {scenario.probability_pct.toFixed(0)} %
        </span>
      </div>
      <p className="mb-2 text-[11px] tabular text-neutral-400">
        Schwelle: <span className="text-neutral-200">{scenario.threshold}</span>
      </p>
      <table className="mb-2 w-full text-[11px]">
        <tbody>
          {rows.map((key) => {
            const r = scenario.asset_reactions[key]!;
            return (
              <tr key={key} className="border-t border-white/5">
                <td className="py-1 pr-2 text-neutral-400">{ASSET_LABELS[key].label}</td>
                <td className="py-1 text-right">
                  <ReactionPill reaction={r} unit={ASSET_LABELS[key].unit} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[11px] leading-snug text-neutral-300">{scenario.one_liner}</p>
    </div>
  );
}

function ReactionPill({
  reaction,
  unit,
}: {
  reaction: { direction: Dir; magnitude_pct: number; confidence: number };
  unit: 'pct' | 'bp';
}) {
  const { direction, magnitude_pct, confidence } = reaction;
  const tone =
    direction === 'up'
      ? 'text-emerald-300'
      : direction === 'down'
        ? 'text-rose-300'
        : 'text-neutral-400';
  const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Calendar;
  const sign = direction === 'up' ? '+' : direction === 'down' ? '−' : '±';
  const value =
    unit === 'bp'
      ? `${sign}${Math.abs(magnitude_pct).toFixed(0)} bp`
      : `${sign}${Math.abs(magnitude_pct).toFixed(1)} %`;
  return (
    <span className={`inline-flex items-center gap-1 font-bold tabular ${tone}`}>
      <Icon className="size-3" />
      {value}
      <span className="text-[9px] font-normal text-neutral-600">· {(confidence * 100).toFixed(0)} %</span>
    </span>
  );
}

function CalendarSubscribeBanner({
  accessToken,
  countries,
}: {
  accessToken: string | null;
  countries: ReadonlyArray<MacroCountry>;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [importance, setImportance] = useState<'all' | 'mid_high' | 'high'>('mid_high');
  if (!accessToken) return null;

  const url = buildCalendarSubscribeUrl(accessToken, { days: 30, countries, importance });
  const webcalUrl = url.replace(/^https?:\/\//, 'webcal://');

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('iCal-URL kopieren:', url);
    }
  }

  return (
    <div className="mb-4 rounded-2xl border border-white/5 bg-white/[0.02] p-3">
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Download className="size-4 text-amber-400" />
          <span className="text-[12px] font-bold uppercase tracking-wider text-amber-300">
            In Kalender abonnieren
          </span>
          <span className="text-[11px] text-neutral-500">
            Outlook · Google · Apple — nächste 30 Tage, Auto-Sync
          </span>
        </div>
        <span className="text-[11px] text-neutral-400">{expanded ? 'einklappen' : 'anzeigen'}</span>
      </button>
      {expanded ? (
        <div className="mt-3 space-y-2">
          {/* Importance-Filter — Doc 2026-05-17: „der ballert alle rein" */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-neutral-500">
              Welche Termine?
            </span>
            <div className="inline-flex overflow-hidden rounded-md border border-white/10 bg-neutral-900/60">
              {(
                [
                  { key: 'high', label: 'Nur Hoch' },
                  { key: 'mid_high', label: 'Wichtig + Hoch' },
                  { key: 'all', label: 'Alle' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setImportance(opt.key)}
                  className={`px-2 py-1 text-[11px] font-medium transition-colors ${
                    importance === opt.key
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'text-neutral-400 hover:bg-white/[0.04] hover:text-neutral-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-[10px] text-neutral-600">
              Default „Wichtig + Hoch" — Low-Releases sind 90 % Lärm.
            </span>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-white/10 bg-neutral-900/60 px-2 py-1.5">
            <code className="flex-1 truncate font-mono text-[11px] text-neutral-300">{url}</code>
            <button
              type="button"
              onClick={() => void copyUrl()}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/25"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
              {copied ? 'Kopiert' : 'Kopieren'}
            </button>
          </div>
          <div className="grid gap-2 text-[11px] md:grid-cols-3">
            <CalHelp
              title="Apple / Outlook"
              steps={[
                <>
                  1-Klick:{' '}
                  <a href={webcalUrl} className="text-amber-300 hover:underline">
                    Jetzt abonnieren (webcal://)
                  </a>
                </>,
                'Apple Calendar bzw. Outlook öffnet + Doc bestätigt.',
              ]}
            />
            <CalHelp
              title="Google Calendar"
              steps={[
                'Andere Kalender → + → Per URL hinzufügen',
                'URL oben einfügen → Kalender hinzufügen',
              ]}
            />
            <CalHelp
              title="Outlook 365 Web"
              steps={[
                'Kalender hinzufügen → Aus dem Internet',
                'URL oben einfügen → Importieren',
              ]}
            />
          </div>
          <p className="text-[10px] text-neutral-500">
            ⚠ Token ist personalisiert — nicht teilen. Kalender-Clients aktualisieren typisch alle 4-12 h.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function CalHelp({ title, steps }: { title: string; steps: ReadonlyArray<React.ReactNode> }) {
  return (
    <div className="rounded-lg border border-white/5 bg-neutral-900/40 p-2">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-400">{title}</div>
      <ol className="space-y-0.5 text-neutral-300">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </div>
  );
}

/** V1-Fallback nur für alte D1-Rows ohne `events`. */
function LegacyScenarioTile({ scenario }: { scenario: MacroScenario }) {
  const labelTone =
    scenario.label === 'beat'
      ? 'text-emerald-300'
      : scenario.label === 'miss'
        ? 'text-rose-300'
        : 'text-neutral-300';
  return (
    <div className="glass rounded-xl p-3">
      <div className="flex items-center justify-between">
        <span className={`text-[12px] font-bold uppercase tracking-wider ${labelTone}`}>
          {SCENARIO_LABEL[scenario.label]}
        </span>
        <span className="rounded bg-neutral-800/60 px-1.5 py-0.5 text-[10px] tabular text-neutral-400">
          {(scenario.confidence * 100).toFixed(0)} %
        </span>
      </div>
      <p className="mt-2 text-[11px] italic text-neutral-500">{scenario.threshold}</p>
      <p className="mt-1 text-[12px] leading-snug text-neutral-300">{scenario.likely_reaction}</p>
    </div>
  );
}
